import React from 'react';
import { StyleSheet, View } from 'react-native';

import { Button, Text } from '~/components/core';
import { FontAwesome5 } from '~/components/core/Icons';
import { useStyles } from '~/hooks';
import { useTheme } from '~/hooks/useTheme';
import { FONT_SIZE_M, FONT_SIZE_S } from '~/constants/typography';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

type Props = {
    onClone: () => void;
    onArchive: () => void;
    canClone: boolean;
};

// Body for failed strategies. Shows a constant generic message — the failed `response` shape
// is unverified (may contain a stacktrace) so it is deliberately not parsed.
// TODO: surface response error detail once BE confirms the failed-response shape
// (docs/ai-strategy/00-overview.md #5).
export default function FailureBody({ onClone, onArchive, canClone }: Props) {
    const styles = useStyles(stylesGenerator);
    const theme = useTheme();
    return (
        <View style={styles.container}>
            <View style={styles.iconCircle}>
                <FontAwesome5 name="exclamation" size={scaleSize(22)} style={styles.icon} />
            </View>
            <Text fontType="LATO_BOLD" style={styles.title}>
                ai-strategy-failed-title
            </Text>
            <Text style={styles.body}>ai-strategy-failed-body</Text>
            {canClone ? (
                <View style={styles.buttonWrap}>
                    <Button title="ai-strategy-action-clone" onPress={onClone} />
                </View>
            ) : null}
            <View style={styles.buttonWrap}>
                <Button
                    title="ai-strategy-action-archive"
                    onPress={onArchive}
                    backgroundColor={{ backgroundColor: theme.backgroundSecondary }}
                    titleColor={theme.text}
                />
            </View>
        </View>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        container: {
            alignItems: 'center',
            paddingVertical: scaleSize(24),
            paddingHorizontal: scaleSize(24),
        },
        iconCircle: {
            width: scaleSize(52),
            height: scaleSize(52),
            borderRadius: scaleSize(26),
            borderWidth: 2,
            borderColor: theme.alert,
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: scaleSize(14),
        },
        icon: {
            color: theme.alert,
        },
        title: {
            fontSize: FONT_SIZE_M,
            color: theme.text,
            textAlign: 'center',
        },
        body: {
            fontSize: FONT_SIZE_S,
            color: theme.textSecondary,
            textAlign: 'center',
            marginTop: scaleSize(8),
            marginBottom: scaleSize(16),
        },
        // width + maxWidth (not alignSelf: 'stretch') so the container's alignItems: 'center' can
        // centre it: a stretched child capped by maxWidth is pinned to the cross-axis start (left)
        // instead of centred.
        buttonWrap: {
            width: '100%',
            maxWidth: scaleSize(300),
            marginTop: scaleSize(8),
        },
    });
